import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Eye, Gift, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ProductOrder {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  total_amount: number;
  status: string;
  order_items: any;
  subtotal: number | null;
  tax_amount: number | null;
  is_new: boolean | null;
}

interface GiftCardOrder {
  id: string;
  created_at: string;
  sender_name: string;
  sender_email: string;
  recipient_name: string;
  recipient_email: string;
  amount: number;
  status: string;
  code: string;
}

export function ProductOrdersTable() {
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [giftCardOrders, setGiftCardOrders] = useState<GiftCardOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<ProductOrder | null>(null);
  const [selectedGiftCard, setSelectedGiftCard] = useState<GiftCardOrder | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [giftCardDetailsOpen, setGiftCardDetailsOpen] = useState(false);
  const navigate = useNavigate();

  const loadOrders = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load product orders
      const { data: productData, error: productError } = await supabase
        .from("product_orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (productError) throw productError;
      setOrders(productData || []);

      // Load gift card orders (only paid ones for billing)
      const { data: giftCardData, error: giftCardError } = await supabase
        .from("gift_cards")
        .select("*")
        .eq("status", "paid")
        .order("created_at", { ascending: false });

      if (giftCardError) throw giftCardError;
      setGiftCardOrders(giftCardData || []);
    } catch (error) {
      console.error("Error loading orders:", error);
      toast.error("Erreur lors du chargement des commandes");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Mark new orders as seen when viewing this tab
  useEffect(() => {
    const markAsSeen = async () => {
      const newOrders = orders.filter(o => o.is_new);
      if (newOrders.length > 0) {
        await supabase
          .from("product_orders")
          .update({ is_new: false })
          .eq("is_new", true);
      }
    };
    
    if (orders.length > 0) {
      markAsSeen();
    }
  }, [orders]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      completed: "default",
      pending: "secondary",
      cancelled: "destructive",
    };

    const labels: Record<string, string> = {
      completed: "Payée",
      pending: "En attente",
      cancelled: "Annulée",
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Commandes Produits</CardTitle>
          <CardDescription>Chargement...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getGiftCardStatusBadge = (status: string) => {
    if (status === "paid") {
      return <Badge className="bg-primary">Payée</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <>
      <Card className="shadow-card bg-gradient-card border-0">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-2xl md:text-3xl font-playfair text-primary flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" />
            Commandes
          </CardTitle>
          <CardDescription>
            Historique des commandes produits et cartes cadeaux
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          <Tabs defaultValue="products" className="space-y-4">
            <TabsList>
              <TabsTrigger value="products" className="gap-2">
                <ShoppingBag className="w-4 h-4" />
                Produits ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="giftcards" className="gap-2">
                <Gift className="w-4 h-4" />
                Cartes Cadeaux ({giftCardOrders.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="products">
              {orders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Aucune commande produit pour le moment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Articles</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} className={order.is_new ? "bg-primary/5" : ""}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {order.is_new && (
                                <Badge className="bg-destructive text-destructive-foreground text-xs">
                                  Nouveau
                                </Badge>
                              )}
                              <div>
                                {new Date(order.created_at).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })}
                                <br />
                                <span className="text-xs text-muted-foreground">
                                  {new Date(order.created_at).toLocaleTimeString("fr-FR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {order.first_name} {order.last_name}
                          </TableCell>
                          <TableCell className="text-sm">
                            {order.email}
                            <br />
                            <span className="text-muted-foreground">{order.phone}</span>
                          </TableCell>
                          <TableCell>
                            {Array.isArray(order.order_items) ? order.order_items.length : 1} article(s)
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {order.total_amount.toFixed(2)}€
                          </TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedOrder(order);
                                setDetailsOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="giftcards">
              {giftCardOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Gift className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Aucune carte cadeau vendue pour le moment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Acheteur</TableHead>
                        <TableHead>Destinataire</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {giftCardOrders.map((card) => (
                        <TableRow key={card.id}>
                          <TableCell className="font-medium">
                            {new Date(card.created_at).toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </TableCell>
                          <TableCell className="font-mono font-semibold">{card.code}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{card.sender_name}</p>
                              <p className="text-sm text-muted-foreground">{card.sender_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{card.recipient_name}</p>
                              <p className="text-sm text-muted-foreground">{card.recipient_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {card.amount.toFixed(2)}€
                          </TableCell>
                          <TableCell>{getGiftCardStatusBadge(card.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedGiftCard(card);
                                setGiftCardDetailsOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Product Order Details Dialog */}
      {selectedOrder && (
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Détails de la commande</DialogTitle>
              <DialogDescription>
                Commande du {new Date(selectedOrder.created_at).toLocaleDateString("fr-FR")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-muted/30 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Client</h3>
                <p className="font-medium">
                  {selectedOrder.first_name} {selectedOrder.last_name}
                </p>
                <p className="text-sm">{selectedOrder.email}</p>
                <p className="text-sm">{selectedOrder.phone}</p>
              </div>

              {/* Order Items */}
              {Array.isArray(selectedOrder.order_items) && (
                <div>
                  <h3 className="font-semibold mb-3">Articles commandés</h3>
                  <div className="space-y-2">
                    {selectedOrder.order_items.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 bg-muted/20 rounded"
                      >
                        <div>
                          <p className="font-medium">{item.product_title}</p>
                          <p className="text-sm text-muted-foreground">
                            Quantité: {item.quantity} × {item.unit_price.toFixed(2)}€
                          </p>
                        </div>
                        <p className="font-bold">{item.total.toFixed(2)}€</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="space-y-2 bg-muted/30 p-4 rounded-lg">
                {selectedOrder.subtotal !== null && (
                  <div className="flex justify-between text-sm">
                    <span>Sous-total HT</span>
                    <span>{selectedOrder.subtotal.toFixed(2)}€</span>
                  </div>
                )}
                {selectedOrder.tax_amount !== null && (
                  <div className="flex justify-between text-sm">
                    <span>TVA (20%)</span>
                    <span>{selectedOrder.tax_amount.toFixed(2)}€</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total TTC</span>
                  <span className="text-secondary">{selectedOrder.total_amount.toFixed(2)}€</span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="font-semibold">Statut:</span>
                {getStatusBadge(selectedOrder.status)}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Gift Card Details Dialog */}
      {selectedGiftCard && (
        <Dialog open={giftCardDetailsOpen} onOpenChange={setGiftCardDetailsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Détails de la carte cadeau
              </DialogTitle>
              <DialogDescription>
                Achetée le {new Date(selectedGiftCard.created_at).toLocaleDateString("fr-FR")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-muted/30 p-4 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Code</p>
                <p className="text-2xl font-mono font-bold text-primary">{selectedGiftCard.code}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Acheteur</h3>
                  <p className="font-medium">{selectedGiftCard.sender_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedGiftCard.sender_email}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Destinataire</h3>
                  <p className="font-medium">{selectedGiftCard.recipient_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedGiftCard.recipient_email}</p>
                </div>
              </div>

              <div className="bg-primary/10 p-4 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Montant</p>
                <p className="text-3xl font-bold text-primary">{selectedGiftCard.amount.toFixed(2)}€</p>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-semibold">Statut:</span>
                {getGiftCardStatusBadge(selectedGiftCard.status)}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
